import { useCallback, useEffect, useMemo, useState } from 'react';
import { AccessGate } from '../components/AccessGate';
import { Garage } from '../components/Garage';
import { GameOver } from '../components/GameOver';
import { HowToPlay } from '../components/HowToPlay';
import { Hud } from '../components/Hud';
import { MainMenu } from '../components/MainMenu';
import { PauseOverlay } from '../components/PauseOverlay';
import { PhaserHost } from '../components/PhaserHost';
import { RUN } from '../games/nganya/config/gameConfig';
import type { GameSceneData } from '../games/nganya/NganyaGame';
import type { HudState, PlayerProgress, RunResult, ScreenId } from '../games/nganya/types';
import { EventBus, Events } from '../games/nganya/utils/EventBus';
import { gameChallengeService } from '../services/GameChallengeService';
import { gameProfileService } from '../services/GameProfileService';
import { gameScoreService } from '../services/GameScoreService';
import { fetchVasAccess, type VasAccessState } from '../services/VasAccessService';
import styles from './App.module.css';

export function App() {
  const [access, setAccess] = useState<VasAccessState>({ status: 'loading' });
  const [screen, setScreen] = useState<ScreenId>('menu');
  const [progress, setProgress] = useState<PlayerProgress>(() => gameProfileService.get());
  const [hud, setHud] = useState<HudState | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);
  const [runRequest, setRunRequest] = useState<(GameSceneData & { requestId: number }) | null>(
    null,
  );
  const daily = useMemo(() => gameChallengeService.getToday(), []);

  const refreshAccess = useCallback(() => {
    setAccess({ status: 'loading' });
    void fetchVasAccess().then(setAccess);
  }, []);

  useEffect(() => {
    refreshAccess();
    const id = window.setInterval(() => {
      void fetchVasAccess().then((next) => {
        if (next.status !== 'allowed') setAccess(next);
      });
    }, 60_000);
    return () => window.clearInterval(id);
  }, [refreshAccess]);

  const beginRun = useCallback(
    (opts: { isDaily: boolean }) => {
      const challenge = opts.isDaily ? gameChallengeService.getToday() : null;
      const data: GameSceneData & { requestId: number } = {
        requestId: Date.now(),
        skinId: progress.selectedSkin,
        isDaily: opts.isDaily,
        seed: challenge?.seed ?? null,
        durationSec: challenge?.durationSec ?? RUN.durationSec,
        reducedEffects:
          progress.reducedEffects ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        soundEnabled: progress.soundEnabled,
        musicEnabled: progress.musicEnabled,
      };
      setResult(null);
      setHud(null);
      setRunRequest(data);
      setScreen('playing');
    },
    [progress],
  );

  useEffect(() => {
    const offHud = EventBus.on(Events.HUD, (payload) => setHud(payload as HudState));
    const offPause = EventBus.on(Events.PAUSE, () => setScreen('paused'));
    const offResume = EventBus.on(Events.RESUME, () => setScreen('playing'));
    const offOver = EventBus.on(Events.GAME_OVER, (payload) => {
      const recorded = gameScoreService.record(
        payload as Omit<RunResult, 'bestScore' | 'isLocalDemo' | 'newAchievements'>,
      );
      setResult(recorded);
      setProgress(gameProfileService.get());
      setScreen('gameover');
    });
    return () => {
      offHud();
      offPause();
      offResume();
      offOver();
    };
  }, []);

  useEffect(() => {
    const prevent = (e: TouchEvent) => {
      if (screen === 'playing' || screen === 'paused') e.preventDefault();
    };
    document.body.addEventListener('touchmove', prevent, { passive: false });
    return () => document.body.removeEventListener('touchmove', prevent);
  }, [screen]);

  const toggleSound = () => {
    setProgress(gameProfileService.update({ soundEnabled: !progress.soundEnabled }));
  };

  const toMenu = () => {
    EventBus.emit(Events.REQUEST_MENU);
    setScreen('menu');
    setHud(null);
    setResult(null);
  };

  const playingVisible =
    screen === 'playing' || screen === 'paused' || screen === 'gameover';

  if (access.status === 'loading') {
    return (
      <div className="app-shell">
        <div className={styles.boot}>Checking Playhub access…</div>
      </div>
    );
  }

  if (access.status === 'denied' || access.status === 'error') {
    return (
      <div className="app-shell">
        <AccessGate
          title={access.status === 'error' ? 'Connection issue' : 'Subscription required'}
          message={access.message}
          subscribeUrl={access.subscribeUrl}
          onRetry={refreshAccess}
        />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <PhaserHost runRequest={runRequest} visible={playingVisible} />

      {screen === 'menu' && (
        <div className={`overlay ${styles.menuLayer}`}>
          <MainMenu
            progress={progress}
            daily={daily}
            onPlay={() => beginRun({ isDaily: false })}
            onDaily={() => beginRun({ isDaily: true })}
            onGarage={() => setScreen('garage')}
            onHowTo={() => setScreen('howto')}
            onToggleSound={toggleSound}
          />
        </div>
      )}

      {screen === 'garage' && (
        <div className={`overlay ${styles.menuLayer}`}>
          <Garage
            progress={progress}
            onSelect={(id) => setProgress(gameProfileService.selectSkin(id))}
            onBack={() => setScreen('menu')}
          />
        </div>
      )}

      {screen === 'howto' && (
        <div className={`overlay ${styles.menuLayer}`}>
          <HowToPlay onBack={() => setScreen('menu')} />
        </div>
      )}

      {(screen === 'playing' || screen === 'paused') && <Hud hud={hud} />}
      {screen === 'paused' && (
        <PauseOverlay
          soundEnabled={progress.soundEnabled}
          onToggleSound={toggleSound}
          onMenu={toMenu}
        />
      )}
      {screen === 'gameover' && result && (
        <GameOver
          result={result}
          onAgain={() => beginRun({ isDaily: result.isDaily })}
          onMenu={toMenu}
        />
      )}
    </div>
  );
}
